<script lang="ts">
import { PropType } from "vue"
import { RawLocation } from "vue-router"
import { AlarmLevel, Alarm } from "@/types/alarm"
import AlarmDetail from "@/components/alarms/AlarmDetail.vue"
import AlarmLevelIcon from "@/components/alarms/AlarmLevelIcon.vue"
import AlarmLevelBadge from "@/components/alarms/AlarmLevelBadge.vue"
import { alarmSource, alarmTypeTitle, formatDate, sourceIcon } from "@/utils/alarm-utils"

export default {
    name: "Notifications",
    components: { AlarmDetail, AlarmLevelIcon, AlarmLevelBadge },
    props: {
        value: { type: Boolean, required: true },
        notifications: {
            type: Array as PropType<Alarm[]>,
            required: true,
        },
        settingsLink: {
            type: [String, Object] as PropType<RawLocation>,
            default: null,
        },
    },
    data() {
        return {
            searchQuery: "",
            selectedType: "",
            selectedLevels: [] as AlarmLevel[],
            selectedAlarm: null as Alarm | null,
        }
    },
    computed: {
        alarmTypes(): string[] {
            return [...new Set(this.notifications.map((alarm) => alarm.type))]
        },
        availableLevels(): AlarmLevel[] {
            return [...new Set(this.notifications.map((alarm) => alarm.level))]
        },
        filteredNotifications(): Alarm[] {
            return this.notifications.filter((alarm) => {
                const matchesType = !this.selectedType || alarm.type === this.selectedType
                const matchesLevel =
                    this.selectedLevels.length === 0 || this.selectedLevels.includes(alarm.level)
                const query = this.searchQuery.toLowerCase()
                const matchesSearch =
                    !query ||
                    [alarm.locationName, alarm.deviceName, alarm.controllerUnitId].some((field) =>
                        field?.toLowerCase().includes(query),
                    )

                return matchesType && matchesLevel && matchesSearch
            })
        },
        alarmCount(): number {
            return this.filteredNotifications.length
        },
    },
    methods: {
        formatDate,
        close() {
            this.$emit("input", false)
        },
        alarmTypeTitle(type: string): string {
            return alarmTypeTitle(this.$i18n, type)
        },
        alarmSourceTitle(source: string): string {
            return alarmSource(this.$i18n, source)
        },
        alarmSourceIcon(source: string): string {
            return sourceIcon(source)
        },
        isLevelSelected(level: AlarmLevel): boolean {
            return this.selectedLevels.includes(level)
        },
        toggleLevel(level: AlarmLevel) {
            const index = this.selectedLevels.indexOf(level)

            if (index === -1) {
                this.selectedLevels.push(level)
            } else {
                this.selectedLevels.splice(index, 1)
            }
        },
        openAlarmDetail(alarm: Alarm) {
            this.selectedAlarm = alarm
        },
        closeAlarmDetail() {
            this.selectedAlarm = null
        },
        handleClickOutside(event: MouseEvent) {
            const modalContent = this.$el.querySelector(".modal-content")

            if (modalContent && !modalContent.contains(event.target as Node)) {
                this.close()
            }
        },
        handleKeyDown(event: KeyboardEvent) {
            if (event.key === "Escape") {
                if (this.selectedAlarm) {
                    this.closeAlarmDetail()
                } else {
                    this.close()
                }
            }
        },
    },
    mounted() {
        document.addEventListener("keydown", this.handleKeyDown)
    },
    beforeDestroy() {
        document.removeEventListener("keydown", this.handleKeyDown)
    },
}
</script>

<template>
    <div class="alarm-notifications" v-if="value">
        <transition name="modal">
            <div
                class="modal-mask d-flex align-items-center justify-content-center"
                @click.self="handleClickOutside"
            >
                <div class="modal-dialog w-100" role="document">
                    <div class="modal-content fixed-size-modal">
                        <div class="modal-header">
                            <h5 class="modal-title">{{ $t("Notifications") }}</h5>
                            <router-link
                                v-if="settingsLink"
                                :to="settingsLink"
                                class="settings-link ml-auto"
                                :title="$t('alarm_notifications.title')"
                                :aria-label="$t('alarm_notifications.title')"
                                @click.native="close"
                            >
                                <i class="fa-solid fa-gear"></i>
                            </router-link>
                            <button
                                type="button"
                                class="close"
                                :class="{ 'ml-3': settingsLink }"
                                aria-label="Close"
                                @click.prevent="close"
                            >
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div class="modal-body" style="overflow: hidden">
                            <template v-if="!selectedAlarm">
                                <div class="filters">
                                    <div class="form-group search-input">
                                        <input
                                            type="text"
                                            class="form-control"
                                            v-model="searchQuery"
                                            :placeholder="
                                                $t('notifications.search_location').toString()
                                            "
                                        />
                                    </div>
                                    <div class="form-group type-dropdown">
                                        <select
                                            class="form-control type-dropdown"
                                            v-model="selectedType"
                                        >
                                            <option value="">
                                                {{ $t("notifications.all_alarms") }}
                                            </option>
                                            <option
                                                v-for="type in alarmTypes"
                                                :key="type"
                                                :value="type"
                                            >
                                                {{ alarmTypeTitle(type) }}
                                            </option>
                                        </select>
                                        <span class="alarm-count">{{ alarmCount }}</span>
                                    </div>
                                    <div class="level-filter">
                                        <AlarmLevelBadge
                                            v-for="level in availableLevels"
                                            :key="level"
                                            :level="level"
                                            :active="isLevelSelected(level)"
                                            interactive
                                            @click="toggleLevel(level)"
                                        />
                                    </div>
                                </div>
                                <div class="content">
                                    <div v-if="filteredNotifications.length > 0">
                                        <div
                                            v-for="alarm in filteredNotifications"
                                            :key="alarm.id"
                                            class="card alarm-card"
                                            @click="openAlarmDetail(alarm)"
                                        >
                                            <div class="card-body d-flex align-items-stretch">
                                                <div class="alarm-card-main flex-grow-1">
                                                    <div class="alarm-header">
                                                        <AlarmLevelIcon :level="alarm.level" />
                                                        <span class="alarm-title">{{
                                                            alarmTypeTitle(alarm.type)
                                                        }}</span>
                                                        <div
                                                            class="alarm-detail-row alarm-header-time"
                                                        >
                                                            <i
                                                                class="fa-solid fa-calendar-days"
                                                            ></i>
                                                            <span
                                                                v-b-tooltip.hover="
                                                                    formatDate(
                                                                        alarm.conditionStartedAt,
                                                                        false,
                                                                    )
                                                                "
                                                                >{{
                                                                    formatDate(
                                                                        alarm.conditionStartedAt,
                                                                    )
                                                                }}</span
                                                            >
                                                        </div>
                                                    </div>

                                                    <div
                                                        v-if="alarm.deviceName"
                                                        class="alarm-detail-row"
                                                    >
                                                        <i
                                                            class="fa-solid fa-plug-circle-xmark"
                                                        ></i>
                                                        <span>{{ alarm.deviceName ?? "-" }}</span>
                                                    </div>
                                                    <div
                                                        v-if="alarm.locationName"
                                                        class="alarm-detail-row"
                                                    >
                                                        <i class="fa-solid fa-house"></i>
                                                        <span>{{ alarm.locationName ?? "-" }}</span>
                                                    </div>
                                                </div>
                                                <i
                                                    class="fa-solid fa-chevron-right alarm-chevron align-self-center"
                                                ></i>
                                            </div>
                                        </div>
                                    </div>
                                    <span v-else class="text-center text-info no-alarms">{{
                                        $t("notifications.no_alarms")
                                    }}</span>
                                </div>
                            </template>
                            <AlarmDetail v-else :alarm="selectedAlarm" @close="closeAlarmDetail" />
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<style lang="scss" scoped>
.card {
    padding: 0;
}

.filters {
    display: flex;
    flex-direction: column;
    flex: 0 0 auto;
}

.form-group {
    margin-bottom: 0.5rem;
}

.fixed-size-modal {
    height: 650px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    border-radius: 0.5rem;
    overflow: hidden;
}

.modal-body {
    padding: 0.5rem;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.content {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
}

.type-dropdown {
    display: flex;
    flex-direction: row;
    gap: 1rem;
    align-items: center;
}

.level-filter {
    display: flex;
    gap: 0.5rem;
    margin-top: 0.5rem;
}

.alarm-count {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    background-color: red;
    color: white;
    border-radius: 50%;
    width: 25px;
    height: 25px;
    line-height: 25px;
    text-align: center;
    font-size: 0.85rem;
}

.alarm-card {
    cursor: pointer;
}

.modal-header {
    align-items: center;
}

.settings-link {
    color: var(--theme-auxiliary);
    font-size: 1.1rem;

    &:hover {
        color: var(--theme-shade);
    }
}

.alarm-chevron {
    color: var(--theme-auxiliary);
    margin-left: 0.5rem;
}

.alarm-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.9rem;
}

.alarm-title {
    font-size: 1.05rem;
    font-weight: 700;
    color: var(--theme-secondary);
}

.alarm-detail-row {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    color: var(--theme-auxiliary);
    font-size: 0.85rem;
    margin-bottom: 0.4rem;

    i {
        width: 1rem;
        text-align: center;
    }

    &:last-child {
        margin-bottom: 0;
    }
}

.alarm-header-time {
    margin-left: auto;
    margin-bottom: 0;
    flex: 0 0 auto;
    white-space: nowrap;

    i {
        display: none;
    }
}

@media (max-width: 576px) {
    .fixed-size-modal {
        width: auto;
    }

    .search-input {
        width: 100%;
    }

    .type-dropdown {
        width: 100%;
    }

    .alarm-header {
        flex-wrap: wrap;
        margin-bottom: 0.4rem;
    }

    .alarm-header-time {
        flex-basis: 100%;
        justify-content: flex-start;
        margin-left: 0;
        margin-top: 0.25rem;

        i {
            display: inline-block;
        }
    }
}
</style>
